import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  EXTENSION,
  FORMAT,
  ORIENTATION,
} from 'src/models/DownloadModel';
import { FORM } from 'src/constants/Form';
import { DIALOGS } from 'src/models/DialogModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { setError } from 'src/redux/actions/errorsActions';
import { useState } from 'react';
import { closeDialog } from 'src/redux/actions/dialogActions';
import {
  postOpenSelectedTabEntityAction,
  postOpenTabEntityAction,
} from 'src/redux/actions/querybuilderActions';
import { PAYMENT_STATUS, PaymentModel } from 'src/models/PaymentModel';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';

enum PaymentType {
  all = 'all',
  selected = 'selected',
  result = 'result',
}

enum PaymentRender {
  payment = 'payment',
  tax = 'tax',
}

const UserDownloadReceiptDialog = () => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const opened =
    useSelector((state: Store) => state.dialog[DIALOGS.userDownloadReceipt]?.opened || false);

  const selections = useSelector(
    (state: Store) => state.selected[ENTITIES.payments] || [],
  );

  const paymentsStatus = useSelector(
    (state: Store) => selections.map(
      (id: number) => {
        if (!state.payments[id]) return undefined;
        return state.payments[id].payment_status;
      },
    )
    || [] as PaymentModel[],
  );

  const options = {
    file: 'payment_receipt.pdf',
    orientation: ORIENTATION.portrait,
    format: FORMAT.pdf,
    delivery: DELIVERY_TYPE.download,
    payment_receipt: true,
  } as DownloadOptionsModel;

  const [type, setType] = useState(PaymentType.selected as PaymentType);
  const [render, setRender] = useState(PaymentRender.tax as PaymentRender);

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.userDownloadReceipt }));
  };

  const validPaymentStatuses = [
    PAYMENT_STATUS.paid,
    PAYMENT_STATUS.cancelled,
    PAYMENT_STATUS.refunded,
  ];

  const handleDownload = () => {
    dispatch(setError({ [FORM.download]: {} }));

    const isPaymentTypeValid = () => paymentsStatus.every(
      (status: PAYMENT_STATUS) => validPaymentStatuses.includes(status),
    );

    if (!isPaymentTypeValid && type === PaymentType.selected && !options.export_to_grid) {
      dispatch(setAlert({
        type: ALERT_TYPE.warning,
        code: t(lang, 'forms.payments.required_paid_payment'),
      }));
      return;
    }
    if (!options.file) {
      options.file = 'receipts';
    }
    options.file = `${options.file.split('.')[0]}.${EXTENSION.pdf}`;
    options.payment_receipt = render === PaymentRender.payment;

    if (type === PaymentType.all) {
      options.all = true;
      dispatch(postOpenTabEntityAction(
        ENTITIES.taxReceipts,
        {
          file: 'receipts.pdf',
          orientation: ORIENTATION.portrait,
          format: FORMAT.pdf,
          delivery: DELIVERY_TYPE.download,
          payment_receipt: false,
        },
      ));
    }
    if (type === PaymentType.selected) {
      options.all = false;
      dispatch(postOpenSelectedTabEntityAction(
        ENTITIES.taxReceipts,
        selections,
        {
          file: 'receipts.pdf',
          orientation: ORIENTATION.portrait,
          format: FORMAT.pdf,
          delivery: DELIVERY_TYPE.download,
          payment_receipt: false,
        },
      ));
    }
    dispatch(closeDialog({ dialog: DIALOGS.paymentDownload }));
  };

  return (
    <Dialog
      aria-labelledby="download-receipts"
      open={opened}>
      <DialogTitle id="download-receipts">Get Receipts</DialogTitle>
      <DialogContent dividers>
        <div style={{ width: '480px' }}>
          <FormControl component="fieldset" style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Payment selection</FormLabel>
            <RadioGroup
              row
              aria-label="payments"
              name="payments"
              value={type}
              onChange={(e) => setType(e.target.value as PaymentType)}>
              <FormControlLabel
                value={PaymentType.all}
                control={<Radio />}
                label="All" />
              <FormControlLabel
                disabled={!selections.length}
                value={PaymentType.selected}
                control={<Radio />}
                label={`Selected (${selections.length})`} />
            </RadioGroup>
          </FormControl>
          <div />
          { !options.export_to_grid && (
            <>
              <FormControl component="fieldset" style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Receipts</FormLabel>
                <RadioGroup
                  row
                  aria-label="render"
                  name="render"
                  value={render}
                  onChange={(e) => setRender(e.target.value as PaymentRender)}>
                  <FormControlLabel
                    value={PaymentRender.payment}
                    control={<Radio />}
                    label={PaymentRender.payment} />
                  <FormControlLabel
                    value={PaymentRender.tax}
                    control={<Radio />}
                    label={PaymentRender.tax} />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          { t(lang, 'misc.cancel') }
        </Button>
        <Button onClick={handleDownload} color="primary">
          { t(lang, 'misc.get') }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDownloadReceiptDialog;
